import { useEffect, useRef, useState } from "react"

// Animated mini-mockup for the Strategise feature row.
// 15s loop: AI scores 6 categories with rationale on row 1 → weighted total → GO decision → win themes.
// Styles live under `.st-*` classes in src/index.css (scoped to .landing-page).

type ScoreRow = {
  key: "s1" | "s2" | "s3" | "s4" | "s5" | "s6"
  fillKey: "f1" | "f2" | "f3" | "f4" | "f5" | "f6"
  category: string
  weight: string
}

const ROWS: [ScoreRow, ScoreRow, ScoreRow, ScoreRow, ScoreRow, ScoreRow] = [
  { key: "s1", fillKey: "f1", category: "Strategic Fit",  weight: "30%" },
  { key: "s2", fillKey: "f2", category: "Capability",     weight: "25%" },
  { key: "s3", fillKey: "f3", category: "Commercial",     weight: "20%" },
  { key: "s4", fillKey: "f4", category: "Risk",           weight: "10%" },
  { key: "s5", fillKey: "f5", category: "Resource",       weight: "8%"  },
  { key: "s6", fillKey: "f6", category: "Relationship",   weight: "7%"  },
]

const THEMES = [
  { key: "tp1" as const, label: "Zero-LTI safety record" },
  { key: "tp2" as const, label: "78% local supply" },
  { key: "tp3" as const, label: "12 of 12 on-time" },
]

function ThemeIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 8 L7 13 L14 4" />
    </svg>
  )
}

function ScoreRowEl({ row }: { row: ScoreRow }) {
  return (
    <div className="st-row">
      <span className="st-cat-name">{row.category}</span>
      <span className="st-weight-chip">{row.weight}</span>
      <span className={`st-score-chip st-${row.key}`}>
        <span className={`st-score-num st-${row.key}`} />
        <span className="st-score-denom">/10</span>
      </span>
      <div className="st-bar"><div className={`st-bar-fill st-${row.fillKey}`} /></div>
    </div>
  )
}

export function StrategiseVisual() {
  const rootRef = useRef<HTMLDivElement>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const obs = new IntersectionObserver(
      (entries) => setInView(entries[0]?.isIntersecting ?? false),
      { threshold: 0.4 },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  return (
    <div ref={rootRef} className={`st-frame ${inView ? "st-playing" : ""}`}>
      {/* Header */}
      <div className="st-head">
        <span className="st-head-title">Go/No-Go Assessment</span>
        <span className="st-ai-indicator">
          <span className="st-dot" />
          <span className="st-ai-text" />
        </span>
      </div>

      {/* Scorecard */}
      <div className="st-scorecard">
        {/* Row 1 + rationale block */}
        <ScoreRowEl row={ROWS[0]} />
        <div className="st-rationale">
          <div className="st-rat-header">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M8 1.5l1.4 3.6 3.6 1.4-3.6 1.4-1.4 3.6-1.4-3.6-3.6-1.4 3.6-1.4z" />
              <path d="M13 11l0.5 1.4 1.4 0.5-1.4 0.5-0.5 1.4-0.5-1.4-1.4-0.5 1.4-0.5z" />
            </svg>
            AI Assessment
            <span className="st-rat-score">8/10</span>
            <span className="st-rat-confidence">high confidence</span>
          </div>
          <div className="st-rat-text">
            Strong fit &mdash; highway infrastructure focus matches your delivered{" "}
            <span className="st-rat-cite">M5 Bridge</span> programme and{" "}
            <span className="st-rat-cite">Northwind Rail</span> signalling renewal.
          </div>
        </div>
        {/* Rows 2-6 */}
        {ROWS.slice(1).map((row) => (
          <ScoreRowEl key={row.key} row={row} />
        ))}
      </div>

      {/* Total + Decision */}
      <div className="st-total-decision">
        <div className="st-total-block">
          <span className="st-total-label">Weighted score</span>
          <span className="st-total-value" />
        </div>
        <div className="st-decision">
          <span className="st-decision-icon">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3,8 7,12 13,4" />
            </svg>
          </span>
          <span className="st-decision-label">GO</span>
          <span className="st-decision-reason">Aligns with your strategic priorities</span>
        </div>
      </div>

      {/* Win themes */}
      <div className="st-themes">
        <span className="st-themes-label">Win themes</span>
        <div className="st-theme-row">
          {THEMES.map((t) => (
            <span key={t.key} className={`st-theme-pill st-${t.key}`}>
              <ThemeIcon />
              {t.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}
